// ============================================================================
// AUTOMATION PACK PPTX EXPORTER
// Themed PowerPoint export for the Advisor Automation Packs tab, covering all
// engines: Jira Automation (A4J), ScriptRunner, JMWE, JWT, ServiceNow, Rovo.
//
// Visual system matches consultingReport-pptx.js / aiContent-pptx.js: a themed
// dark cover with soft glows and a rule-count panel, light body slides with a
// footer + page number master, kicker/title/accent headers, and tinted cards.
//
// Design goals vs. the previous builder:
//   • One clean, readable slide per rule (no separate cramped "artifact" slide)
//   • Trigger shown as type + separate JQL / schedule / transition rows so long
//     JQL never crams into a tiny box
//   • Actions & conditions as tidy bullet lists, right-sized text
//   • Importable JSON summarised as key/value rows in a dark side panel when it
//     fits, with a note that the full JSON is in the JSON export
//   • Engine-agnostic normaliser
// ============================================================================

import PptxGenJS from 'pptxgenjs';
import { THEME_BACKGROUNDS, LOGO } from '@process-advisor/process-miner/consultant/consultingReport-pptx-assets';
import { getReportTheme, themeFromPlatform, DEFAULT_REPORT_THEME } from '@process-advisor/process-miner/consultant/reportThemes';

const BODY = 'Arial';
const HEAD = 'Arial';
const PAGE = { w: 13.333, h: 7.5, mx: 0.5, my: 0.4 };
const CW = PAGE.w - PAGE.mx * 2;
const BOTTOM = 6.9;

// ── Utilities ────────────────────────────────────────────────────────────────
const s = (v) => {
  if (v == null) return '';
  if (typeof v === 'string') return v.replace(/\s+/g, ' ').trim();
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return '';
};
const list = (v) => (Array.isArray(v) ? v : v == null ? [] : [v]);
const chunk = (arr, n) => { const out = []; for (let i = 0; i < arr.length; i += n) out.push(arr.slice(i, i + n)); return out; };
const titleCase = (t) => String(t || '').replace(/[._-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

function clipTo(v, max) {
  const t = s(v);
  if (t.length <= max) return t;
  const cut = t.slice(0, Math.max(max - 1, 4));
  return cut.replace(/\s+\S*$/, '') + '…';
}

// ── Theme-scoped builder ─────────────────────────────────────────────────────
export function buildAutomationPackPptx(packData, packTitle, packKey, themeId, opts = {}) {
  const theme = getReportTheme(themeId || themeFromPlatform(opts.platform));
  const C = { ...theme.pptx };
  const BG = THEME_BACKGROUNDS[theme.id] || THEME_BACKGROUNDS.servicenow;
  const brandSuffix = theme.brandSuffix;
  const modelTitle = s(opts.modelTitle) || 'Process Model';

  const TONE = {
    danger:  { col: C.danger,  bg: C.dangerBg,  border: C.dangerBorder },
    warn:    { col: C.warn,    bg: C.warnBg,    border: C.warnBorder },
    success: { col: C.success, bg: C.successBg, border: C.successBorder },
    primary: { col: C.primary, bg: C.tint,      border: C.tintBorder },
  };
  const toneFor = (v) => {
    const t = s(v).toLowerCase();
    if (/critical|high/.test(t)) return 'danger';
    if (/medium|moderate/.test(t)) return 'warn';
    if (/low|ready|enabled/.test(t)) return 'success';
    return 'primary';
  };

  // ── Primitives ─────────────────────────────────────────────────────────────
  const shadow = () => ({ type: 'outer', color: '122A35', blur: 6, offset: 1.5, angle: 90, opacity: 0.06 });
  const card = (sl, x, y, w, h, fill = C.white, border = C.border, accent) => {
    sl.addShape('roundRect', { x, y, w, h, fill: { color: fill }, line: { color: border, width: 0.8 }, rectRadius: 0.05, shadow: shadow() });
    if (accent) sl.addShape('rect', { x, y: y + 0.09, w: 0.05, h: h - 0.18, fill: { color: accent } });
  };
  const label = (sl, text, x, y, w, color = C.muted) =>
    sl.addText(s(text).toUpperCase(), { x, y, w, h: 0.2, fontSize: 8.2, bold: true, charSpacing: 1.1, fontFace: BODY, color, margin: 0 });
  const chip = (sl, x, y, text, tone = 'primary') => {
    const t = TONE[tone] || TONE.primary;
    const w = Math.min(Math.max(s(text).length * 0.078 + 0.32, 0.7), 1.9);
    sl.addText(clipTo(text, 26), { x, y, w, h: 0.3, shape: 'roundRect', rectRadius: 0.06, align: 'center', valign: 'middle', fontSize: 9, bold: true, fontFace: BODY, color: t.col, fill: { color: t.bg }, line: { color: t.border, width: 0.8 }, margin: 0 });
    return w;
  };
  const dots = (sl, ax, ay, color) => {
    for (let r = 0; r < 8; r++) for (let c = 0; c < 13; c++)
      sl.addShape('ellipse', { x: ax + c * 0.22, y: ay + r * 0.2, w: 0.025, h: 0.025, fill: { color, transparency: 8 }, line: { color, transparency: 100 } });
  };
  const brand = (sl, dark) => {
    try { if (LOGO) sl.addImage({ data: LOGO, x: PAGE.mx, y: 0.55, w: 0.46, h: 0.46 }); } catch (_) {}
    sl.addText([
      { text: 'Process Advisor', options: { color: dark ? C.white : C.navy, bold: true } },
      { text: ` ${brandSuffix}`, options: { color: dark ? C.snGreenBright : C.snGreen, bold: true } },
    ], { x: PAGE.mx + 0.56, y: 0.66, w: 5, h: 0.26, fontSize: 14.5, fontFace: HEAD, margin: 0 });
  };
  const bullets = (sl, items, x, y, w, h, { max = 5, fontSize = 9, maxChars = 150, color = C.text } = {}) => {
    const rows = list(items).map(s).filter(Boolean).slice(0, max)
      .map((it) => ({ text: clipTo(it, maxChars), options: { bullet: { code: '2022', indent: 10 }, color, fontSize, fontFace: BODY, paraSpaceAfter: 5, lineSpacing: fontSize * 1.32 } }));
    if (rows.length) sl.addText(rows, { x, y, w, h, valign: 'top', margin: 0 });
    return rows.length;
  };

  // ── Engine detection & normalisation ─────────────────────────────────────────
  const engineKey = (() => {
    if (packKey === 'rovo') return 'rovo';
    if (packData?.source_system === 'servicenow' || String(packData?.pack_title || '').startsWith('ServiceNow')) return 'servicenow';
    if (packKey === 'scriptrunner' || packData?.rules?.[0]?.rule_id?.toString().startsWith('SR') || packData?.rules?.[0]?.script) return 'scriptrunner';
    if (packKey === 'jmwe' || packData?.rules?.[0]?.jmwe_actions) return 'jmwe';
    if (packKey === 'jwt' || packData?.automation_opportunities || packData?.rules?.[0]?.jwt_actions) return 'jwt';
    return 'jira';
  })();

  const findExportObj = (rule) => rule?.export?.a4j_cloud || rule?.jira_automation_json || rule?.export || null;

  // titleCase is for type tokens ("jira.issue.transition"), not sentences.
  // Running it on prose mangles apostrophes and abbreviations ("Record'S", "E G ,").
  const isProse = (t) => /\s/.test(String(t || '').trim());
  const smartLabel = (t) => isProse(t) ? s(t) : titleCase(t);

  const describeAction = (a) => {
    if (!a) return '';
    if (typeof a === 'string') return smartLabel(a);
    const type = smartLabel(a.type || a.component || 'Action');
    const cfg = a.config || a.value || {};
    const bits = [];
    if (cfg.transition_name) bits.push(`→ ${cfg.transition_name}`);
    if (cfg.transition?.name) bits.push(`→ ${cfg.transition.name}`);
    if (cfg.comment) { const c = s(cfg.comment?.body || cfg.comment?.value || cfg.comment); if (c) bits.push(`“${clipTo(c, 90)}”`); }
    if (cfg.fields) bits.push(`fields: ${Object.keys(cfg.fields).join(', ')}`);
    if (cfg.field) bits.push(`${s(cfg.field)} = ${s(cfg.value)}`);
    if (cfg.url) bits.push(`webhook`);
    return [type, ...bits].filter(Boolean).join(' ');
  };

  // Trigger -> { type, rows: [[label, value]] } so long JQL sits on its own row.
  const describeTrigger = (trigger) => {
    if (!trigger) return { type: '', rows: [] };
    if (typeof trigger === 'string') return { type: smartLabel(trigger), rows: [] };
    const cfg = trigger.config || trigger.value || {};
    const type = smartLabel(trigger.type || trigger.component || 'Trigger');
    const rows = [];
    const jql = cfg.jql || cfg.queryString;
    const cron = cfg.cron || cfg.schedule?.cron;
    if (jql) rows.push(['JQL', s(jql)]);
    if (cron) rows.push(['Schedule', s(cron)]);
    if (cfg.from_status || cfg.to_status || cfg.from || cfg.to) {
      const from = cfg.from_status || (Array.isArray(cfg.from) ? cfg.from.join(', ') : cfg.from) || '*';
      const to = cfg.to_status || (Array.isArray(cfg.to) ? cfg.to.join(', ') : cfg.to) || '*';
      rows.push(['Transition', `${from} → ${to}`]);
    }
    return { type, rows };
  };

  const compactJsonRows = (obj) => {
    if (!obj || typeof obj !== 'object') return [];
    const trigger = obj.trigger || {};
    const value = trigger.value || {};
    const components = Array.isArray(obj.components) ? obj.components : [];
    return [
      ['State', obj.state],
      ['Trigger', titleCase(trigger.type || trigger.component)],
      ['Schedule', s(value.schedule?.cron || value.cron)],
      ['JQL', s(value.jql || value.queryString)],
      ['Actions', components.filter((c) => c.component === 'ACTION').map((c) => titleCase(c.type)).filter(Boolean).join(', ')],
      ['Conditions', components.filter((c) => c.component === 'CONDITION').map((c) => titleCase(c.type)).filter(Boolean).join(', ')],
    ].filter((r) => s(r[1]));
  };

  const normalise = () => {
    if (engineKey === 'rovo') {
      return list(packData?.agents).map((a, i) => ({
        id: `Agent ${i + 1}`, name: s(a.name) || `Rovo Agent ${i + 1}`,
        priority: a.priority, purposeLabel: 'Purpose',
        purpose: s(a.purpose || a.expected_benefit || a.evidence_signal),
        trigger: { type: s(a.trigger), rows: [] },
        conditionsLabel: 'Prerequisites', conditions: list(a.prerequisites).map(s),
        actions: list(a.actions).map(s),
        benefit: s(a.expected_benefit), evidence: s(a.evidence_signal),
        pairing: s(a.jira_automation_pairing), instructions: s(a.agent_instructions),
        exportObj: a.jira_automation_json || null,
        meta: [s(a.rovo_product), a.priority && `Priority: ${a.priority}`, a.setup_effort && `Setup: ${a.setup_effort}`, a.requires_studio && 'Rovo Studio'].filter(Boolean),
      }));
    }
    if (engineKey === 'scriptrunner') {
      return list(packData?.rules).map((r, i) => {
        const trig = r.trigger || {};
        const trigType = titleCase(trig.type || 'Trigger') + (trig.events ? `: ${list(trig.events).join(', ')}` : '');
        const rows = [];
        if (trig.schedule) rows.push(['Schedule', s(trig.schedule)]);
        if (r.jql_scope) rows.push(['JQL Scope', s(r.jql_scope)]);
        return {
          id: r.rule_id || `SR-${i + 1}`, name: s(r.name) || `ScriptRunner Rule ${i + 1}`,
          priority: r.priority, purposeLabel: 'Rule intent', purpose: s(r.intent || r.description),
          trigger: { type: trigType, rows },
          conditionsLabel: 'When', conditions: list(r.when).map(s),
          actions: list(r.actions).map(s),
          benefit: s(r.expected_benefit) || list(r.telemetry).map(s).join(' '),
          notes: list(r.risk_notes).map(s),
          codePanel: r.script ? {
            title: `Script — ${s(r.script.language) || 'groovy'}`,
            pseudo: s(r.script.pseudo),
            code: s(r.script.code),
          } : null,
          meta: ['ScriptRunner', r.priority, r.script?.language].filter(Boolean),
        };
      });
    }
    if (engineKey === 'jmwe') {
      const describeJmweAction = (a) => {
        const type = titleCase(a.type || 'Action');
        const cfg = a.config || {};
        const bits = [type];
        if (a.name) bits.push(a.name);
        const detail = [];
        if (cfg.field) detail.push(`field: ${s(cfg.field)}`);
        if (cfg.transition) detail.push(`→ ${s(cfg.transition)}`);
        if (cfg.errorMessage) detail.push(`“${clipTo(cfg.errorMessage, 70)}”`);
        return { head: bits.join(': '), detail: detail.join('  ·  ') };
      };
      return list(packData?.rules).map((r, i) => {
        const acts = list(r.jmwe_actions).map(describeJmweAction);
        return {
          id: r.rule_id || `JMWE-${i + 1}`, name: s(r.name) || `JMWE Rule ${i + 1}`,
          priority: r.priority, purposeLabel: 'Rationale', purpose: s(r.rationale || r.intent || r.description),
          trigger: { type: list(r.target_transitions).map((t) => `${t.from} → ${t.to}${t.label ? ` (${t.label})` : ''}`).join(', '), rows: [] },
          conditionsLabel: 'Preconditions', conditions: r.preconditions?.jql ? [`JQL: ${r.preconditions.jql}`] : [],
          actions: acts.map((a) => a.detail ? `${a.head} — ${a.detail}` : a.head),
          benefit: list(r.success_criteria).map(s).join(' '),
          notes: list(r.risks).map(s),
          codePanel: list(r.jmwe_actions).length ? {
            title: 'JMWE Configuration',
            rows: list(r.jmwe_actions).map((a) => [titleCase(a.type), [a.name, a.config?.field && `field: ${a.config.field}`, a.config?.transition && `transition: ${a.config.transition}`].filter(Boolean).join(' · ')]),
            templates: list(r.jmwe_actions).filter((a) => a.config?.value && /\{[%{]/.test(a.config.value)).map((a) => ({ label: a.config.field || a.name, value: a.config.value })),
          } : null,
          meta: ['JMWE', r.priority].filter(Boolean),
        };
      });
    }
    if (engineKey === 'jwt') {
      const describeJwtAction = (a) => {
        if (!a) return '';
        if (typeof a === 'string') return titleCase(a);
        const type = titleCase(a.type || 'Action');
        const name = s(a.name || a.summary || a.label || type);
        const cfg = a.config || {};
        const details = [];
        if (cfg.field) details.push(`field: ${s(cfg.field)}`);
        if (cfg.transition) details.push(`→ ${s(cfg.transition)}`);
        if (cfg.transition_name) details.push(`→ ${s(cfg.transition_name)}`);
        if (cfg.cron_expression || cfg.cron) details.push(`schedule: ${s(cfg.cron_expression || cfg.cron)}`);
        if (cfg.jql_scope || cfg.jql) details.push(`JQL: ${clipTo(cfg.jql_scope || cfg.jql, 82)}`);
        if (cfg.expression) details.push(`expression: ${clipTo(cfg.expression, 90)}`);
        if (cfg.errorMessage) details.push(`“${clipTo(cfg.errorMessage, 72)}”`);
        if (Array.isArray(cfg.actions)) details.push(`actions: ${cfg.actions.map(describeJwtAction).filter(Boolean).join(', ')}`);
        return [type, name, details.length ? `— ${details.join(' · ')}` : ''].filter(Boolean).join(': ').replace(': —', ' —');
      };

      const normaliseTransition = (t) => {
        if (!t) return '';
        if (typeof t === 'string') return t;
        return [t.from || t.source || '', t.to || t.target || ''].filter(Boolean).join(' → ') || s(t.transition_name || t.name || t.label);
      };

      const rules = list(packData?.rules).length ? list(packData?.rules) : list(packData?.automation_opportunities);
      return rules.map((r, i) => {
        const legacy = !!r.jwt_implementation;
        const impl = r.jwt_implementation || {};
        const impact = r.expected_impact || {};
        const riskObj = r.risk_and_considerations || {};
        const rawActions = legacy ? [] : list(r.jwt_actions);
        const transCfg = list(impl.transition_configuration);
        const schedRules = list(impl.scheduled_rules);
        const expressionRows = list(impl.expression_snippets);

        let trigType = '';
        const trigRows = [];
        if (legacy) {
          if (transCfg.length) trigType = transCfg.map((t) => s(t.transition_name)).filter(Boolean).join(', ');
          else if (schedRules.length) {
            trigType = 'Scheduled';
            if (schedRules[0].schedule_description || schedRules[0].cron_expression) trigRows.push(['Schedule', s(schedRules[0].schedule_description || schedRules[0].cron_expression)]);
            if (schedRules[0].jql_scope) trigRows.push(['JQL Scope', s(schedRules[0].jql_scope)]);
          }
        } else {
          trigType = list(r.target_transitions).map(normaliseTransition).filter(Boolean).join(', ');
          if (!trigType) {
            const scheduled = rawActions.find((a) => a && typeof a === 'object' && String(a.type || '').toLowerCase().includes('scheduled'));
            if (scheduled) trigType = 'Scheduled rule';
          }
          const jql = r.preconditions?.jql || r.jql_scope;
          if (jql) trigRows.push(['JQL Scope', s(jql)]);
          const scheduled = rawActions.find((a) => a && typeof a === 'object' && String(a.type || '').toLowerCase().includes('scheduled'));
          const cfg = scheduled?.config || {};
          if (cfg.cron_expression || cfg.cron) trigRows.push(['Schedule', s(cfg.cron_expression || cfg.cron)]);
          if (cfg.jql_scope || cfg.jql) trigRows.push(['JQL Scope', s(cfg.jql_scope || cfg.jql)]);
        }

        const legacyConds = [
          ...transCfg.flatMap((t) => [...list(t.conditions), ...list(t.validators)].map(s)),
          ...list(impl.preconditions).map(s),
        ].filter(Boolean);
        const conds = legacy
          ? legacyConds
          : [r.preconditions?.jql ? `JQL: ${r.preconditions.jql}` : '', r.preconditions?.notes, ...list(r.risks).map(s)].filter(Boolean);

        const legacyActs = [
          ...transCfg.flatMap((t) => list(t.post_functions).map(s)),
          ...schedRules.flatMap((sr) => list(sr.actions).map(s)),
        ].filter(Boolean);
        const actions = legacy ? (legacyActs.length ? legacyActs : list(impl.configuration_steps).map(s)) : rawActions.map(describeJwtAction).filter(Boolean);

        const configSteps = legacy ? list(impl.configuration_steps).map(s) : [
          ...rawActions.map(describeJwtAction).filter(Boolean),
          ...list(r.configuration_steps).map(s),
        ].filter(Boolean);
        const templates = legacy
          ? expressionRows.map((e) => ({ label: e.purpose, value: e.expression }))
          : rawActions
              .map((a) => a?.config)
              .filter(Boolean)
              .flatMap((cfg) => [
                cfg.expression ? { label: 'Expression', value: cfg.expression } : null,
                cfg.errorMessage ? { label: 'Error message', value: cfg.errorMessage } : null,
                cfg.jql_scope || cfg.jql ? { label: 'JQL', value: cfg.jql_scope || cfg.jql } : null,
              ].filter(Boolean));

        return {
          id: r.rule_id || r.id || `JWT-${i + 1}`,
          name: s(r.name || r.title) || `JWT Rule ${i + 1}`,
          priority: r.priority || riskObj.risk_level,
          purposeLabel: legacy ? 'Problem & goal' : 'Rationale',
          purpose: legacy
            ? ([s(r.problem_statement), s(r.goal)].filter(Boolean).join(' → ') || s(impl.summary))
            : s(r.rationale || r.intent || r.description || r.problem_statement || r.goal),
          trigger: { type: trigType || 'JWT workflow control', rows: trigRows },
          conditionsLabel: 'Conditions',
          conditions: conds,
          actions: actions.length ? actions : ['Configure the JWT validator, condition, post-function or scheduled rule described by this rule.'],
          benefit: s(impact.impact_summary) || list(r.success_criteria).map(s).join(' '),
          notes: legacy ? list(riskObj.risks).map(s) : list(r.risks).map(s),
          codePanel: (configSteps.length || templates.length) ? {
            title: 'JWT Configuration',
            steps: configSteps,
            templates,
          } : null,
          meta: ['JWT', r.priority, (riskObj.risk_level || r.risk_level) && `Risk: ${riskObj.risk_level || r.risk_level}`].filter(Boolean),
        };
      });
    }
    if (engineKey === 'servicenow') {
      return list(packData?.rules).map((r, i) => {
        const impl = r.implementation || {};
        const evidence = list(r.evidence).map(s).filter(Boolean);
        const implRows = [
          ['Table', s(impl.target_table)],
          ['Area', s(impl.configuration_area || r.capability)],
          ['Fields', list(impl.fields_used).map(s).filter(Boolean).join(', ')],
        ].filter(([, v]) => v);
        const implSteps = list(impl.steps).map(s).filter(Boolean);
        return {
          id: r.id || r.rule_id || `SN-${i + 1}`, name: s(r.name) || `ServiceNow Automation ${i + 1}`,
          priority: r.priority || r.risk_level, purposeLabel: 'Purpose', purpose: s(r.purpose || r.description || r.intent),
          trigger: { type: s(r.trigger?.type || r.trigger), rows: r.trigger?.condition ? [['Condition', s(r.trigger.condition)]] : [] },
          conditionsLabel: 'Conditions', conditions: list(r.conditions).map(s),
          actions: list(r.actions).map(describeAction),
          benefit: s(r.expected_benefit) || evidence.join('  ·  '),
          conditions2: list(r.requirements).map(s), instructions: '',
          codePanel: (implRows.length || implSteps.length) ? {
            title: s(r.implementation_label) || 'ServiceNow Configuration',
            rows: implRows,
            steps: implSteps,
          } : null,
          meta: ['ServiceNow', s(r.capability), r.priority, r.setup_effort ? `Effort: ${r.setup_effort}` : null].filter(Boolean),
        };
      });
    }
    // jira (A4J)
    return list(packData?.rules).map((r, i) => {
      const exportObj = findExportObj(r);
      // Conditions may live at rule level OR inside the export components (JQL conditions).
      const ruleConds = list(r.conditions).map((c) => c?.config?.queryString || c?.value?.queryString || titleCase(c?.type) || s(c));
      const exportConds = list(exportObj?.components)
        .filter((c) => c.component === 'CONDITION')
        .map((c) => s(c.value?.queryString || c.value?.jql) || titleCase(c.type));
      const conditions = [...ruleConds, ...exportConds].filter(Boolean);
      return {
        id: r.id || `RULE-${i + 1}`, name: s(r.name) || `Jira Automation Rule ${i + 1}`,
        priority: r.risk_level ? `Risk: ${r.risk_level}` : r.priority,
        priorityTone: r.risk_level, purposeLabel: 'Purpose', purpose: s(r.description || r.purpose),
        trigger: describeTrigger(r.triggers?.[0] || r.trigger || exportObj?.trigger),
        conditionsLabel: 'Conditions', conditions,
        actions: list(r.actions).map(describeAction),
        benefit: s(r.expected_benefit || r.evidence),
        conditions2: list(r.requirements || r.risks).map(s),
        exportObj,
        meta: ['Jira Automation', r.risk_level && `Risk: ${r.risk_level}`, r.priority].filter(Boolean),
      };
    });
  };

  const items = normalise();
  const isRovo = engineKey === 'rovo';
  const itemNoun = isRovo ? 'Agent' : 'Rule';
  const deckTitle = isRovo ? (s(packData?.pack_title) || 'Rovo Agent Pack') : (s(packTitle) || s(packData?.pack_title) || 'Automation Pack');
  const summary = s(packData?.source_context?.analysis_summary || packData?.summary || packData?.description || packData?.pack_summary || packData?.rovo_readiness_rationale);

  const packReqs = list(packData?.technical_requirements || packData?.requirements || packData?.prerequisites)
    .map((r) => (typeof r === 'string' ? r : s(r.requirement || r))).filter(Boolean);
  const defaultJiraReqs = engineKey === 'jira'
    ? ['Jira Cloud with Automation for Jira enabled', 'Project Admin permission to import automation rules', 'Import via Project Settings → Automation → Import rule']
    : [];
  const reqs = packReqs.length ? packReqs : defaultJiraReqs;

  // ── Deck setup ───────────────────────────────────────────────────────────────
  const pptx = new PptxGenJS();
  pptx.defineLayout({ name: 'WIDE', width: PAGE.w, height: PAGE.h });
  pptx.layout = 'WIDE';
  pptx.author = 'Process Advisor';
  pptx.company = 'Metricus';
  pptx.title = `${deckTitle} — ${modelTitle}`;

  pptx.defineSlideMaster({
    title: 'PACKBODY',
    background: { data: BG.BG_BODY },
    objects: [{ text: { text: `Process Advisor ${brandSuffix}  ·  ${deckTitle}  ·  ${modelTitle}`, options: { x: PAGE.mx, y: 7.15, w: 10, h: 0.25, fontSize: 8.5, fontFace: BODY, color: C.muted } } }],
    slideNumber: { x: 12.6, y: 7.15, fontSize: 8.5, fontFace: BODY, color: C.muted },
  });

  const header = (sl, kicker, title) => {
    dots(sl, 10.45, 0.22, C.dotGrid);
    sl.addText(s(kicker).toUpperCase(), { x: PAGE.mx, y: PAGE.my, w: CW, h: 0.22, fontSize: 9.5, fontFace: BODY, color: C.primary, bold: true, charSpacing: 1.5, margin: 0 });
    sl.addText(s(title), { x: PAGE.mx, y: PAGE.my + 0.28, w: CW, h: 0.45, fontSize: 18, fontFace: HEAD, color: C.navy, bold: true, valign: 'top', margin: 0 });
    sl.addShape('rect', { x: PAGE.mx, y: PAGE.my + 0.82, w: 1.05, h: 0.045, fill: { color: C.snGreen }, line: { color: C.snGreen } });
    return PAGE.my + 1.05;
  };

  // ── Cover ─────────────────────────────────────────────────────────────────
  const cover = pptx.addSlide();
  cover.background = { data: BG.BG_COVER };
  dots(cover, 10.25, 0.18, C.dotGridDark);
  dots(cover, 0.2, 6.15, C.dotGridDark);
  brand(cover, true);

  const panelX = 9.35, panelW = PAGE.w - panelX - PAGE.mx;
  const lw = panelX - PAGE.mx - 0.45;
  card(cover, panelX, 1.72, panelW, 4.1, C.white, C.border);
  cover.addText(String(items.length), { x: panelX, y: 2.72, w: panelW, h: 0.82, fontSize: 60, bold: true, fontFace: HEAD, color: C.navy, align: 'center', margin: 0 });
  cover.addText(`${itemNoun.toUpperCase()}${items.length === 1 ? '' : 'S'}`, { x: panelX, y: 3.78, w: panelW, h: 0.22, fontSize: 10, bold: true, color: C.muted, charSpacing: 1.7, align: 'center', fontFace: BODY, margin: 0 });
  if (isRovo && packData?.rovo_readiness)
    cover.addText(`Rovo readiness: ${s(packData.rovo_readiness)}`, { x: panelX + 0.2, y: 4.35, w: panelW - 0.4, h: 0.9, fontSize: 9.5, fontFace: BODY, color: C.text, align: 'center', valign: 'top', lineSpacing: 12.5, margin: 0 });

  cover.addText(deckTitle, { x: PAGE.mx, y: 1.95, w: lw, h: 0.95, fontSize: 30, fontFace: HEAD, color: C.white, bold: true, valign: 'top', margin: 0, lineSpacing: 34 });
  cover.addShape('rect', { x: PAGE.mx, y: 3.05, w: 1.15, h: 0.055, fill: { color: C.snGreenBright }, line: { color: C.snGreenBright } });
  cover.addText(modelTitle, { x: PAGE.mx, y: 3.42, w: lw, h: 0.35, fontSize: 17.5, fontFace: BODY, color: C.coverLight, margin: 0 });
  if (summary) cover.addText(clipTo(summary, 560), { x: PAGE.mx, y: 4.1, w: lw, h: 2.0, fontSize: 10.7, fontFace: BODY, color: C.coverLight, valign: 'top', lineSpacing: 15, margin: 0 });
  cover.addText(`Prepared for Metricus    ·    ${new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}`, { x: PAGE.mx, y: 6.72, w: lw, h: 0.3, fontSize: 10.2, fontFace: BODY, color: C.coverMuted, margin: 0 });

  // ── Overview slide: pack summary + rule index ───────────────────────────────
  const kicker = `Process Advisor  ·  ${deckTitle}`;
  const ov = pptx.addSlide({ masterName: 'PACKBODY' });
  let top = header(ov, kicker, '1. Pack Overview');
  if (summary) {
    card(ov, PAGE.mx, top, CW, 0.86, C.tint, C.tintBorder, C.primary);
    label(ov, 'Summary', PAGE.mx + 0.18, top + 0.12, 2, C.primary);
    ov.addText(clipTo(summary, 430), { x: PAGE.mx + 0.18, y: top + 0.34, w: CW - 0.36, h: 0.46, fontSize: 9.6, fontFace: BODY, color: C.text, valign: 'top', lineSpacing: 12.5, margin: 0 });
    top += 1.06;
  }
  // index cards
  const idxCols = 2, idxGap = 0.2;
  const idxCardW = (CW - idxGap) / idxCols;
  const idxRows = Math.ceil(Math.min(items.length, 8) / idxCols);
  const idxAreaH = (reqs.length ? 5.9 : BOTTOM) - top - 0.1;
  const idxCardH = Math.min(0.82, (idxAreaH - idxGap * (idxRows - 1)) / Math.max(idxRows, 1));
  items.slice(0, 8).forEach((r, i) => {
    const x = PAGE.mx + (i % idxCols) * (idxCardW + idxGap);
    const y = top + 0.05 + Math.floor(i / idxCols) * (idxCardH + idxGap);
    const tone = toneFor(r.priorityTone || r.priority);
    card(ov, x, y, idxCardW, idxCardH, C.white, C.border, TONE[tone].col);
    ov.addText(`${i + 1}. ${clipTo(r.name, 60)}`, { x: x + 0.16, y: y + 0.1, w: idxCardW - (r.priority ? 1.5 : 0.32), h: 0.24, fontSize: 9.5, bold: true, fontFace: HEAD, color: C.navy, valign: 'top', margin: 0 });
    if (r.priority) chip(ov, x + idxCardW - 1.32, y + 0.1, r.priority, tone);
    ov.addText(clipTo(r.purpose || r.trigger?.type || '', 118), { x: x + 0.16, y: y + 0.4, w: idxCardW - 0.32, h: idxCardH - 0.5, fontSize: 8, fontFace: BODY, color: C.muted, valign: 'top', lineSpacing: 10.3, margin: 0 });
  });
  if (reqs.length) {
    const ry = 5.98;
    card(ov, PAGE.mx, ry, CW, 0.78, C.card, C.border, C.warn);
    label(ov, 'Technical Requirements', PAGE.mx + 0.18, ry + 0.12, 2.5, C.warn);
    bullets(ov, reqs, PAGE.mx + 0.18, ry + 0.34, CW - 0.36, 0.38, { max: 4, fontSize: 8, maxChars: 118 });
  }

  // ── One slide per rule ──────────────────────────────────────────────────────
  items.forEach((r, idx) => {
    const sl = pptx.addSlide({ masterName: 'PACKBODY' });
    let y = header(sl, kicker, `${idx + 2}. ${clipTo(r.name, 70)}`);
    if (r.meta?.length) {
      sl.addText(r.meta.join('  ·  '), { x: PAGE.mx, y: y - 0.06, w: CW, h: 0.22, fontSize: 8.5, bold: true, fontFace: BODY, color: C.primary, margin: 0 });
      y += 0.24;
    }

    const hasJson = !!r.exportObj;
    const hasCode = !!r.codePanel;
    const hasPanel = hasJson || hasCode;
    const leftW = hasPanel ? 7.35 : CW;

    // Purpose card
    if (r.purpose) {
      card(sl, PAGE.mx, y, leftW, 0.88, C.tint, C.tintBorder, C.primary);
      label(sl, r.purposeLabel || 'Purpose', PAGE.mx + 0.18, y + 0.12, 2.5, C.primary);
      sl.addText(clipTo(r.purpose, 320), { x: PAGE.mx + 0.18, y: y + 0.34, w: leftW - 0.36, h: 0.46, fontSize: 9.2, fontFace: BODY, color: C.text, valign: 'top', lineSpacing: 12.3, margin: 0 });
      y += 1.06;
    }

    // Trigger card (type + rows)
    const trigRows = r.trigger?.rows || [];
    const trigH = 0.6 + trigRows.length * 0.42;
    card(sl, PAGE.mx, y, leftW, trigH, C.white, C.border, C.primary);
    label(sl, 'Trigger', PAGE.mx + 0.18, y + 0.13, 2, C.primary);
    sl.addText(clipTo(r.trigger?.type || 'Not specified', 80), { x: PAGE.mx + 1.5, y: y + 0.12, w: leftW - 1.7, h: 0.24, fontSize: 9.5, bold: true, fontFace: BODY, color: C.navy, valign: 'top', margin: 0 });
    trigRows.forEach(([lbl, val], i) => {
      const ry = y + 0.44 + i * 0.42;
      label(sl, lbl, PAGE.mx + 0.18, ry, 1.2, C.muted);
      sl.addText(clipTo(val, 150), { x: PAGE.mx + 1.5, y: ry - 0.02, w: leftW - 1.7, h: 0.4, fontSize: 8.6, fontFace: 'Courier New', color: C.text, valign: 'top', lineSpacing: 11, margin: 0 });
    });
    y += trigH + 0.14;

    // Actions + Conditions two-up
    const remaining = BOTTOM - y - 0.05;
    const colGap = 0.18;
    const acW = (leftW - colGap) / 2;
    const conds = r.conditions?.length ? r.conditions : [];
    card(sl, PAGE.mx, y, acW, remaining, C.white, C.border, C.success);
    label(sl, 'Actions', PAGE.mx + 0.16, y + 0.14, acW - 0.3, C.success);
    bullets(sl, r.actions, PAGE.mx + 0.16, y + 0.4, acW - 0.32, remaining - 0.5, { max: 6, fontSize: 8.6, maxChars: 120 });

    const rcx = PAGE.mx + acW + colGap;
    // Right column shows Conditions if present, else Expected Benefit / Evidence.
    if (conds.length) {
      card(sl, rcx, y, acW, remaining, C.white, C.border, C.warn);
      label(sl, r.conditionsLabel || 'Conditions', rcx + 0.16, y + 0.14, acW - 0.3, C.warn);
      const yLeft = y + 0.4;
      const hLeft = remaining - 0.5;
      const nShown = bullets(sl, conds, rcx + 0.16, yLeft, acW - 0.32, hLeft, { max: 4, fontSize: 8.6, maxChars: 118 });
      // If conditions are short and there's benefit text, append it below.
      const be = r.benefit || r.evidence;
      if (be && nShown <= 2) {
        const byy = yLeft + nShown * 0.34 + 0.14;
        label(sl, 'Expected Benefit', rcx + 0.16, byy, acW - 0.3, C.accent);
        sl.addText(clipTo(be, 200), { x: rcx + 0.16, y: byy + 0.22, w: acW - 0.32, h: y + remaining - byy - 0.32, fontSize: 8.4, fontFace: BODY, color: C.text, valign: 'top', lineSpacing: 11, margin: 0 });
      }
    } else {
      // Truthful fallback chain: benefit -> evidence -> notes -> requirements.
      // If nothing real exists, render the requirements card or no card at all —
      // never generic filler text.
      const be = r.benefit || r.evidence || (r.notes?.length ? r.notes.join(' ') : '');
      if (be) {
        card(sl, rcx, y, acW, remaining, C.white, C.border, C.accent);
        label(sl, 'Expected Benefit / Evidence', rcx + 0.16, y + 0.14, acW - 0.3, C.accent);
        sl.addText(clipTo(be, 300), { x: rcx + 0.16, y: y + 0.4, w: acW - 0.32, h: (r.notes?.length && (r.benefit || r.evidence) ? (remaining - 0.5) * 0.55 : remaining - 0.5), fontSize: 8.7, fontFace: BODY, color: C.text, valign: 'top', lineSpacing: 12, margin: 0 });
        // If we have both benefit and notes, show notes as risks below.
        if (r.notes?.length && (r.benefit || r.evidence)) {
          const ny = y + 0.4 + (remaining - 0.5) * 0.55 + 0.12;
          label(sl, 'Notes / Risks', rcx + 0.16, ny, acW - 0.3, C.muted);
          bullets(sl, r.notes, rcx + 0.16, ny + 0.22, acW - 0.32, y + remaining - ny - 0.3, { max: 2, fontSize: 8, maxChars: 100, color: C.muted });
        }
      } else if (r.conditions2?.length) {
        card(sl, rcx, y, acW, remaining, C.white, C.border, C.warn);
        label(sl, 'Requirements / Prerequisites', rcx + 0.16, y + 0.14, acW - 0.3, C.warn);
        bullets(sl, r.conditions2, rcx + 0.16, y + 0.4, acW - 0.32, remaining - 0.5, { max: 5, fontSize: 8.4, maxChars: 118 });
      }
    }

    // Dark side panel: importable JSON (Jira/Rovo) or script/config (SR/JMWE/JWT)
    if (hasPanel) {
      const jx = PAGE.mx + leftW + 0.18;
      const jw = CW - leftW - 0.18;
      const jTop = (r.meta?.length ? PAGE.my + 1.29 : PAGE.my + 1.05);
      const jHeight = BOTTOM - jTop - 0.05;
      card(sl, jx, jTop, jw, jHeight, '1E2432', '38445A', C.primary);

      if (hasJson) {
        label(sl, isRovo ? 'Paired Jira JSON' : 'Importable JSON', jx + 0.16, jTop + 0.16, jw - 0.32, 'B9D6FF');
        const rows = compactJsonRows(r.exportObj);
        let ry = jTop + 0.46;
        rows.forEach(([lbl, val]) => {
          sl.addText(lbl.toUpperCase(), { x: jx + 0.16, y: ry, w: jw - 0.32, h: 0.16, fontSize: 6.8, bold: true, charSpacing: 0.8, fontFace: BODY, color: '8FA7CC', margin: 0 });
          const vh = lbl === 'JQL' ? 0.6 : 0.3;
          sl.addText(clipTo(val, 140), { x: jx + 0.16, y: ry + 0.17, w: jw - 0.32, h: vh, fontSize: 7.6, fontFace: lbl === 'JQL' || lbl === 'Schedule' ? 'Courier New' : BODY, color: 'E8EEFF', valign: 'top', lineSpacing: 9.5, margin: 0 });
          ry += 0.17 + vh + 0.08;
        });
        sl.addText('Full importable JSON is in the JSON export.', { x: jx + 0.16, y: BOTTOM - 0.42, w: jw - 0.32, h: 0.36, fontSize: 7, italic: true, fontFace: BODY, color: '8FA7CC', valign: 'bottom', lineSpacing: 9, margin: 0 });
      } else {
        const cp = r.codePanel;
        label(sl, cp.title || 'Implementation', jx + 0.16, jTop + 0.16, jw - 0.32, 'B9D6FF');
        let ry = jTop + 0.46;
        const inW = jw - 0.32;
        const noteY = BOTTOM - 0.38;
        // key/value rows (JMWE config)
        list(cp.rows).forEach(([lbl, val]) => {
          if (ry > noteY - 0.4) return;
          sl.addText(s(lbl).toUpperCase(), { x: jx + 0.16, y: ry, w: inW, h: 0.15, fontSize: 6.6, bold: true, charSpacing: 0.6, fontFace: BODY, color: '8FA7CC', margin: 0 });
          sl.addText(clipTo(val, 90), { x: jx + 0.16, y: ry + 0.16, w: inW, h: 0.28, fontSize: 7.4, fontFace: BODY, color: 'E8EEFF', valign: 'top', lineSpacing: 9, margin: 0 });
          ry += 0.5;
        });
        // configuration steps (JWT)
        if (list(cp.steps).length && ry < noteY - 0.6) {
          sl.addText('CONFIGURATION STEPS', { x: jx + 0.16, y: ry, w: inW, h: 0.15, fontSize: 6.6, bold: true, charSpacing: 0.6, fontFace: BODY, color: '8FA7CC', margin: 0 });
          ry += 0.2;
          const rows = list(cp.steps).slice(0, 5).map((st) => ({ text: clipTo(st, 90), options: { bullet: { code: '2022', indent: 8 }, color: 'E8EEFF', fontSize: 7.3, fontFace: BODY, paraSpaceAfter: 3, lineSpacing: 9.5 } }));
          sl.addText(rows, { x: jx + 0.16, y: ry, w: inW, h: Math.min(1.7, noteY - ry - 0.1), valign: 'top', margin: 0 });
          ry += Math.min(1.7, rows.length * 0.34) + 0.1;
        }
        // pseudo (ScriptRunner)
        if (cp.pseudo && ry < noteY - 0.6) {
          sl.addText('LOGIC', { x: jx + 0.16, y: ry, w: inW, h: 0.15, fontSize: 6.6, bold: true, charSpacing: 0.6, fontFace: BODY, color: '8FA7CC', margin: 0 });
          sl.addText(clipTo(cp.pseudo, 320), { x: jx + 0.16, y: ry + 0.18, w: inW, h: noteY - ry - 0.28, fontSize: 7.4, fontFace: BODY, color: 'E8EEFF', valign: 'top', lineSpacing: 10, margin: 0 });
          ry = noteY;
        }
        // templates / expressions (JMWE Nunjucks, JWT expressions)
        list(cp.templates).forEach((t) => {
          if (ry > noteY - 0.5) return;
          sl.addText(clipTo(t.label, 40).toUpperCase(), { x: jx + 0.16, y: ry, w: inW, h: 0.15, fontSize: 6.4, bold: true, charSpacing: 0.5, fontFace: BODY, color: '8FA7CC', margin: 0 });
          sl.addText(clipTo(t.value, 200), { x: jx + 0.16, y: ry + 0.16, w: inW, h: Math.min(0.9, noteY - ry - 0.2), fontSize: 6.6, fontFace: 'Courier New', color: 'C9D8F0', valign: 'top', lineSpacing: 8.5, margin: 0 });
          ry += 0.85;
        });
        const noteText = cp.code ? 'Full script is in the JSON export.' : 'Full configuration is in the JSON export.';
        sl.addText(noteText, { x: jx + 0.16, y: BOTTOM - 0.36, w: jw - 0.32, h: 0.3, fontSize: 7, italic: true, fontFace: BODY, color: '8FA7CC', valign: 'bottom', lineSpacing: 9, margin: 0 });
      }
    }
  });

  // ── Closing checklist ───────────────────────────────────────────────────────
  const fin = pptx.addSlide({ masterName: 'PACKBODY' });
  const ftop = header(fin, kicker, `${items.length + 2}. Implementation Checklist`);
  card(fin, PAGE.mx, ftop, CW, 0.9, C.tint, C.tintBorder, C.primary);
  label(fin, 'How to use this pack', PAGE.mx + 0.18, ftop + 0.13, 2.5, C.primary);
  fin.addText(`This deck summarises the generated ${deckTitle} in a client-readable format. Use the JSON export for the full import/configuration artifacts.`, { x: PAGE.mx + 0.18, y: ftop + 0.35, w: CW - 0.36, h: 0.48, fontSize: 9.4, fontFace: BODY, color: C.text, valign: 'top', lineSpacing: 12.5, margin: 0 });

  const checklist = [
    `Review each ${itemNoun.toLowerCase()} against local workflow configuration and field names.`,
    'Validate triggers, conditions and actions in a sandbox or development project first.',
    'Confirm permissions, app availability and any required marketplace apps.',
    isRovo ? 'Deploy paired Jira Automation rules via A4J alongside each agent.' : 'Use the JSON export for direct rule import where supported.',
    'Run a small pilot before enabling broadly, and monitor exceptions after release.',
  ];
  const cy = ftop + 1.1;
  card(fin, PAGE.mx, cy, CW, BOTTOM - cy - 0.05, C.white, C.border, C.success);
  label(fin, 'Recommended validation steps', PAGE.mx + 0.18, cy + 0.16, 3, C.success);
  bullets(fin, checklist, PAGE.mx + 0.18, cy + 0.46, CW - 0.36, BOTTOM - cy - 0.6, { max: 6, fontSize: 10.5, maxChars: 160 });

  return pptx;
}

export default buildAutomationPackPptx;
